import { shadows } from '@alison/ui';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { CreativeScoreComponentRow } from './CreativeScoreComponentRow';
import {
  type CreativeScore,
  creativeScoreComponentLabel,
  orderedScoreComponents,
  scoreTier,
} from './data/creativeScore';

const OPEN_DELAY_MS = 200;

const SCORE_CARD_WIDTH = 280;

const SCORE_POPOVER_SX = { pointerEvents: 'none' };

const SCORE_PAPER_SX = {
  pointerEvents: 'auto',
  width: SCORE_CARD_WIDTH,
  maxWidth: '90vw',
  p: 3,
  bgcolor: 'background.paper',
  color: 'text.primary',
  borderRadius: 1,
  boxShadow: shadows.dropdown,
};

interface CreativeScorePopoverProps {
  creativeScore: CreativeScore;
  leadingKpiLabel: string;
  children: ReactNode;
}

function CreativeScorePanel({
  creativeScore,
  leadingKpiLabel,
}: {
  creativeScore: CreativeScore;
  leadingKpiLabel: string;
}): ReactNode {
  const theme = useTheme();
  const components = orderedScoreComponents(creativeScore.components);
  const labels = creativeScoreComponentLabel(leadingKpiLabel);
  const tier = creativeScore.score === null ? null : scoreTier(creativeScore.score);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
          Creative Performance Score
        </Typography>
        {creativeScore.score !== null && tier !== null && (
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: theme.palette.score[tier].main }}>
            {Math.round(creativeScore.score)}
          </Typography>
        )}
      </Box>
      <Typography sx={{ mt: 1, mb: 3, fontSize: 12, color: 'text.secondary' }}>
        Applicable metrics are weighted equally to create a balanced 0–100 score.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {components.map(([name, component]) => (
          <CreativeScoreComponentRow
            key={name}
            name={name}
            component={component}
            label={labels[name]}
          />
        ))}
      </Box>
    </Box>
  );
}

export function CreativeScorePopover({
  creativeScore,
  leadingKpiLabel,
  children,
}: CreativeScorePopoverProps): ReactNode {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(openTimer.current), []);

  function handleCellEnter(event: MouseEvent<HTMLElement>): void {
    const target = event.currentTarget;
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setAnchorEl(target), OPEN_DELAY_MS);
  }

  function handlePaperEnter(event: MouseEvent<HTMLElement>): void {
    clearTimeout(openTimer.current);
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    clearTimeout(openTimer.current);
    setAnchorEl(null);
  }

  return (
    <Box sx={{ display: 'inline-flex' }} onMouseEnter={handleCellEnter} onMouseLeave={handleClose}>
      {children}
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableRestoreFocus
        sx={SCORE_POPOVER_SX}
        slotProps={{
          paper: { sx: SCORE_PAPER_SX, onMouseEnter: handlePaperEnter, onMouseLeave: handleClose },
        }}
      >
        <CreativeScorePanel creativeScore={creativeScore} leadingKpiLabel={leadingKpiLabel} />
      </Popover>
    </Box>
  );
}
